/**
 * Ambient Audio Engine v2.0 - Enhanced Web Audio API sound generation
 * Creates rich, immersive ambient soundscapes with multiple layers:
 * - Base tones (oscillators with harmonics)
 * - Texture layers (filtered noise, granular effects)
 * - Nature-inspired elements (rain, wind, birds, waves)
 * - Evolving pads with slow modulation
 * - Word-specific sonic characteristics
 */

// Audio context singleton
let audioContext = null;
let masterGain = null;
let currentNodes = [];
let isPlaying = false;
let currentMood = null;

// Initialize audio context
export const initAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
    masterGain = audioContext.createGain();
    masterGain.connect(audioContext.destination);
    masterGain.gain.value = 0;
  }
  return audioContext;
};

// ===== NOISE GENERATORS =====

// Create pink noise for warm atmosphere
const createPinkNoise = (ctx, duration = 2) => {
  const bufferSize = ctx.sampleRate * duration;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
  for (let i = 0; i < bufferSize; i++) {
    const white = Math.random() * 2 - 1;
    b0 = 0.99886 * b0 + white * 0.0555179;
    b1 = 0.99332 * b1 + white * 0.0750759;
    b2 = 0.96900 * b2 + white * 0.1538520;
    b3 = 0.86650 * b3 + white * 0.3104856;
    b4 = 0.55000 * b4 + white * 0.5329522;
    b5 = -0.7616 * b5 - white * 0.0168980;
    data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
    b6 = white * 0.115926;
  }
  return buffer;
};

// Create brown noise for deep rumble
const createBrownNoise = (ctx, duration = 2) => {
  const bufferSize = ctx.sampleRate * duration;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  
  let lastOut = 0;
  for (let i = 0; i < bufferSize; i++) {
    const white = Math.random() * 2 - 1;
    data[i] = (lastOut + (0.02 * white)) / 1.02;
    lastOut = data[i];
    data[i] *= 3.5;
  }
  return buffer;
};

// Create white noise for texture
const createWhiteNoise = (ctx, duration = 2) => {
  const bufferSize = ctx.sampleRate * duration;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  return buffer;
};

// ===== NATURE SOUND GENERATORS =====

// Rain simulation - filtered noise with droplet impulses
const createRainLayer = (ctx, intensity = 0.5) => {
  const nodes = [];
  
  // Continuous rain backdrop (filtered pink noise)
  const rainBuffer = createPinkNoise(ctx, 4);
  const rainSource = ctx.createBufferSource();
  rainSource.buffer = rainBuffer;
  rainSource.loop = true;
  
  const rainFilter = ctx.createBiquadFilter();
  rainFilter.type = 'highpass';
  rainFilter.frequency.value = 2000 + (intensity * 2000);
  rainFilter.Q.value = 0.5;
  
  const rainFilter2 = ctx.createBiquadFilter();
  rainFilter2.type = 'lowpass';
  rainFilter2.frequency.value = 8000;
  
  const rainGain = ctx.createGain();
  rainGain.gain.value = 0.08 * intensity;
  
  rainSource.connect(rainFilter);
  rainFilter.connect(rainFilter2);
  rainFilter2.connect(rainGain);
  
  nodes.push({ source: rainSource, gain: rainGain, type: 'rain' });
  
  return nodes;
};

// Wind simulation - modulated filtered noise
const createWindLayer = (ctx, intensity = 0.5) => {
  const nodes = [];
  
  const windBuffer = createBrownNoise(ctx, 4);
  const windSource = ctx.createBufferSource();
  windSource.buffer = windBuffer;
  windSource.loop = true;
  
  const windFilter = ctx.createBiquadFilter();
  windFilter.type = 'bandpass';
  windFilter.frequency.value = 300 + (intensity * 400);
  windFilter.Q.value = 1.5;
  
  // LFO for wind gusts
  const windLfo = ctx.createOscillator();
  const windLfoGain = ctx.createGain();
  windLfo.type = 'sine';
  windLfo.frequency.value = 0.1 + (Math.random() * 0.1);
  windLfoGain.gain.value = 200;
  windLfo.connect(windLfoGain);
  windLfoGain.connect(windFilter.frequency);
  
  const windGain = ctx.createGain();
  windGain.gain.value = 0.1 * intensity;
  
  windSource.connect(windFilter);
  windFilter.connect(windGain);
  
  nodes.push({ source: windSource, gain: windGain, lfo: windLfo, type: 'wind' });
  
  return nodes;
};

// Ocean waves simulation
const createWavesLayer = (ctx, intensity = 0.5) => {
  const nodes = [];
  
  const waveBuffer = createBrownNoise(ctx, 6);
  const waveSource = ctx.createBufferSource();
  waveSource.buffer = waveBuffer;
  waveSource.loop = true;
  
  const waveFilter = ctx.createBiquadFilter();
  waveFilter.type = 'lowpass';
  waveFilter.frequency.value = 400;
  waveFilter.Q.value = 2;
  
  // Slow LFO for wave rhythm
  const waveLfo = ctx.createOscillator();
  const waveLfoGain = ctx.createGain();
  waveLfo.type = 'sine';
  waveLfo.frequency.value = 0.08; // ~7.5 second wave cycle
  waveLfoGain.gain.value = 0.15;
  
  const waveGain = ctx.createGain();
  waveGain.gain.value = 0.12 * intensity;
  
  // Amplitude modulation for wave swell
  waveLfo.connect(waveLfoGain);
  waveLfoGain.connect(waveGain.gain);
  
  waveSource.connect(waveFilter);
  waveFilter.connect(waveGain);
  
  nodes.push({ source: waveSource, gain: waveGain, lfo: waveLfo, type: 'waves' });
  
  return nodes;
};

// Birds/chirps simulation (for peaceful moods)
const createBirdsLayer = (ctx, intensity = 0.3) => {
  const nodes = [];
  
  // Create chirp-like sounds with high-frequency oscillators
  const numBirds = Math.floor(2 + intensity * 3);
  
  for (let i = 0; i < numBirds; i++) {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = 2000 + (Math.random() * 2000); // 2-4kHz
    
    // Frequency wobble for natural sound
    const freqLfo = ctx.createOscillator();
    const freqLfoGain = ctx.createGain();
    freqLfo.type = 'sine';
    freqLfo.frequency.value = 4 + (Math.random() * 8); // 4-12 Hz wobble
    freqLfoGain.gain.value = 100 + (Math.random() * 100);
    freqLfo.connect(freqLfoGain);
    freqLfoGain.connect(osc.frequency);
    
    // Amplitude envelope for chirps
    const ampLfo = ctx.createOscillator();
    const ampLfoGain = ctx.createGain();
    ampLfo.type = 'sine';
    ampLfo.frequency.value = 0.3 + (Math.random() * 0.5); // Random chirp timing
    ampLfoGain.gain.value = 0.02 * intensity;
    
    const birdGain = ctx.createGain();
    birdGain.gain.value = 0;
    
    ampLfo.connect(ampLfoGain);
    ampLfoGain.connect(birdGain.gain);
    
    osc.connect(birdGain);
    
    nodes.push({ source: osc, gain: birdGain, lfo: freqLfo, ampLfo: ampLfo, type: 'bird' });
  }
  
  return nodes;
};

// Thunder rumble (for tense moods)
const createThunderLayer = (ctx, intensity = 0.5) => {
  const nodes = [];
  
  const thunderBuffer = createBrownNoise(ctx, 3);
  const thunderSource = ctx.createBufferSource();
  thunderSource.buffer = thunderBuffer;
  thunderSource.loop = true;
  
  const thunderFilter = ctx.createBiquadFilter();
  thunderFilter.type = 'lowpass';
  thunderFilter.frequency.value = 100 + (intensity * 100);
  thunderFilter.Q.value = 3;
  
  // Occasional rumble swells
  const rumbleLfo = ctx.createOscillator();
  const rumbleLfoGain = ctx.createGain();
  rumbleLfo.type = 'sine';
  rumbleLfo.frequency.value = 0.05; // Very slow
  rumbleLfoGain.gain.value = 0.1;
  
  const thunderGain = ctx.createGain();
  thunderGain.gain.value = 0.15 * intensity;
  
  rumbleLfo.connect(rumbleLfoGain);
  rumbleLfoGain.connect(thunderGain.gain);
  
  thunderSource.connect(thunderFilter);
  thunderFilter.connect(thunderGain);
  
  nodes.push({ source: thunderSource, gain: thunderGain, lfo: rumbleLfo, type: 'thunder' });
  
  return nodes;
};

// ===== MUSICAL ELEMENTS =====

// Create evolving pad with multiple harmonics
const createPadLayer = (ctx, baseFreq, waveform, intensity) => {
  const nodes = [];
  const harmonics = [1, 2, 3, 4, 5];
  const harmonicGains = [1, 0.5, 0.25, 0.125, 0.0625];
  
  harmonics.forEach((harmonic, idx) => {
    const osc = ctx.createOscillator();
    osc.type = waveform;
    osc.frequency.value = baseFreq * harmonic;
    
    // Slight detune for richness
    osc.detune.value = (Math.random() - 0.5) * 10;
    
    // Slow pitch drift
    const pitchLfo = ctx.createOscillator();
    const pitchLfoGain = ctx.createGain();
    pitchLfo.type = 'sine';
    pitchLfo.frequency.value = 0.05 + (Math.random() * 0.05);
    pitchLfoGain.gain.value = 3;
    pitchLfo.connect(pitchLfoGain);
    pitchLfoGain.connect(osc.detune);
    
    const oscGain = ctx.createGain();
    oscGain.gain.value = harmonicGains[idx] * 0.08 * intensity;
    
    osc.connect(oscGain);
    
    nodes.push({ source: osc, gain: oscGain, lfo: pitchLfo, type: 'pad' });
  });
  
  return nodes;
};

// Create drone bass
const createDroneLayer = (ctx, baseFreq, intensity) => {
  const nodes = [];
  
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.value = baseFreq;
  
  // Sub oscillator
  const subOsc = ctx.createOscillator();
  subOsc.type = 'sine';
  subOsc.frequency.value = baseFreq / 2;
  
  // Slow wobble
  const lfo = ctx.createOscillator();
  const lfoGain = ctx.createGain();
  lfo.type = 'sine';
  lfo.frequency.value = 0.1;
  lfoGain.gain.value = 2;
  lfo.connect(lfoGain);
  lfoGain.connect(osc.frequency);
  lfoGain.connect(subOsc.frequency);
  
  const droneGain = ctx.createGain();
  droneGain.gain.value = 0.06 * intensity;
  
  const subGain = ctx.createGain();
  subGain.gain.value = 0.04 * intensity;
  
  osc.connect(droneGain);
  subOsc.connect(subGain);
  
  nodes.push({ source: osc, gain: droneGain, lfo: lfo, type: 'drone' });
  nodes.push({ source: subOsc, gain: subGain, type: 'sub' });
  
  return nodes;
};

// Create shimmer/sparkle layer
const createShimmerLayer = (ctx, baseFreq, intensity) => {
  const nodes = [];
  const numVoices = 4;
  
  for (let i = 0; i < numVoices; i++) {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = baseFreq * (4 + i); // High harmonics
    osc.detune.value = (Math.random() - 0.5) * 20;
    
    // Random amplitude fluttering
    const ampLfo = ctx.createOscillator();
    const ampLfoGain = ctx.createGain();
    ampLfo.type = 'sine';
    ampLfo.frequency.value = 0.2 + (Math.random() * 0.5);
    ampLfoGain.gain.value = 0.015 * intensity;
    
    const shimmerGain = ctx.createGain();
    shimmerGain.gain.value = 0;
    
    ampLfo.connect(ampLfoGain);
    ampLfoGain.connect(shimmerGain.gain);
    
    osc.connect(shimmerGain);
    
    nodes.push({ source: osc, gain: shimmerGain, lfo: ampLfo, type: 'shimmer' });
  }
  
  return nodes;
};

// ===== MOOD PROFILES =====

const MOOD_PROFILES = {
  // RED QUADRANT: High Energy + Negative - Tense, urgent, storm-like
  red: {
    baseFrequency: 110, // A2
    waveform: 'sawtooth',
    layers: ['drone', 'pad', 'wind', 'thunder'],
    filterFreq: 600,
    filterQ: 2,
    color: { r: 255, g: 100, b: 100 }
  },
  
  // YELLOW QUADRANT: High Energy + Positive - Bright, uplifting, birds singing
  yellow: {
    baseFrequency: 220, // A3
    waveform: 'sine',
    layers: ['pad', 'shimmer', 'birds'],
    filterFreq: 2500,
    filterQ: 0.5,
    color: { r: 255, g: 220, b: 100 }
  },
  
  // BLUE QUADRANT: Low Energy + Negative - Deep, somber, rain
  blue: {
    baseFrequency: 65.41, // C2
    waveform: 'triangle',
    layers: ['drone', 'rain', 'wind'],
    filterFreq: 350,
    filterQ: 1.5,
    color: { r: 100, g: 150, b: 255 }
  },
  
  // GREEN QUADRANT: Low Energy + Positive - Peaceful, warm, ocean waves
  green: {
    baseFrequency: 130.81, // C3
    waveform: 'sine',
    layers: ['pad', 'waves', 'shimmer'],
    filterFreq: 1500,
    filterQ: 0.3,
    color: { r: 100, g: 220, b: 150 }
  },
  
  // NEUTRAL
  neutral: {
    baseFrequency: 174.61, // F3
    waveform: 'sine',
    layers: ['pad', 'drone'],
    filterFreq: 1000,
    filterQ: 0.5,
    color: { r: 180, g: 180, b: 180 }
  }
};

// Word-specific intensity modifiers
const WORD_INTENSITY_MAP = {
  // Extreme words = higher intensity modifier
  enraged: 1.5, panicked: 1.4, ecstatic: 1.4, serene: 0.6, drained: 0.5,
  furious: 1.3, anxious: 1.2, elated: 1.3, peaceful: 0.6, exhausted: 0.5,
  stressed: 1.2, worried: 1.1, happy: 1.0, calm: 0.7, tired: 0.6,
  tense: 1.1, concerned: 1.0, content: 0.8, relaxed: 0.7, bored: 0.5,
  // Default is 1.0
};

// Adjective to quadrant mapping
const ADJECTIVE_QUADRANTS = {
  // Red quadrant - High energy, negative
  enraged: 'red', panicked: 'red', stressed: 'red', jittery: 'red', shocked: 'red',
  livid: 'red', furious: 'red', frustrated: 'red', tense: 'red', stunned: 'red',
  fuming: 'red', frightened: 'red', angry: 'red', nervous: 'red', restless: 'red',
  anxious: 'red', apprehensive: 'red', worried: 'red', irritated: 'red', annoyed: 'red',
  repulsed: 'red', troubled: 'red', concerned: 'red', uneasy: 'red', peeved: 'red',
  turbulent: 'red', volatile: 'red', agitated: 'red', alarmed: 'red', distressed: 'red',
  
  // Yellow quadrant - High energy, positive
  surprised: 'yellow', upbeat: 'yellow', festive: 'yellow', exhilarated: 'yellow', ecstatic: 'yellow',
  hyper: 'yellow', cheerful: 'yellow', motivated: 'yellow', inspired: 'yellow', elated: 'yellow',
  energized: 'yellow', lively: 'yellow', enthusiastic: 'yellow', optimistic: 'yellow', excited: 'yellow',
  pleased: 'yellow', happy: 'yellow', focused: 'yellow', proud: 'yellow', thrilled: 'yellow',
  pleasant: 'yellow', joyful: 'yellow', hopeful: 'yellow', playful: 'yellow', blissful: 'yellow',
  vibrant: 'yellow', radiant: 'yellow', euphoric: 'yellow', gleeful: 'yellow', jubilant: 'yellow',
  
  // Blue quadrant - Low energy, negative
  disgusted: 'blue', glum: 'blue', disappointed: 'blue', down: 'blue', apathetic: 'blue',
  pessimistic: 'blue', morose: 'blue', discouraged: 'blue', sad: 'blue', bored: 'blue',
  alienated: 'blue', miserable: 'blue', lonely: 'blue', disheartened: 'blue', tired: 'blue',
  despondent: 'blue', depressed: 'blue', sullen: 'blue', exhausted: 'blue', fatigued: 'blue',
  despair: 'blue', hopeless: 'blue', desolate: 'blue', spent: 'blue', drained: 'blue',
  melancholic: 'blue', somber: 'blue', gloomy: 'blue', weary: 'blue', forlorn: 'blue',
  
  // Green quadrant - Low energy, positive
  'at ease': 'green', easygoing: 'green', content: 'green', loving: 'green', fulfilled: 'green',
  calm: 'green', secure: 'green', satisfied: 'green', grateful: 'green', touched: 'green',
  relaxed: 'green', chill: 'green', restful: 'green', blessed: 'green', balanced: 'green',
  mellow: 'green', thoughtful: 'green', peaceful: 'green', comfy: 'green', carefree: 'green',
  sleepy: 'green', complacent: 'green', tranquil: 'green', cozy: 'green', serene: 'green',
  harmonious: 'green', gentle: 'green', soothing: 'green', placid: 'green', zen: 'green',
};

// Get quadrant for an adjective
export const getQuadrantForMood = (adjective) => {
  return ADJECTIVE_QUADRANTS[adjective?.toLowerCase()] || 'neutral';
};

// ===== MAIN PLAYBACK FUNCTIONS =====

// Stop all current sounds with fade
export const stopAmbientSound = (fadeTime = 1.5) => {
  if (!audioContext || !isPlaying) return;
  
  const now = audioContext.currentTime;
  
  // Fade out master gain
  masterGain.gain.setValueAtTime(masterGain.gain.value, now);
  masterGain.gain.linearRampToValueAtTime(0, now + fadeTime);
  
  // Schedule cleanup
  setTimeout(() => {
    currentNodes.forEach(node => {
      try { 
        if (node.source) node.source.stop(); 
        if (node.lfo) node.lfo.stop();
        if (node.ampLfo) node.ampLfo.stop();
      } catch (e) {}
    });
    currentNodes = [];
    isPlaying = false;
    currentMood = null;
  }, fadeTime * 1000 + 100);
};

// Play ambient sound for a mood
export const playAmbientSound = (adjective, intensity = 0.5, volume = 0.2) => {
  // Initialize audio context if needed
  const ctx = initAudioContext();
  
  // Resume context if suspended (browser autoplay policy)
  if (ctx.state === 'suspended') {
    ctx.resume();
  }
  
  // Check if same mood is already playing
  if (isPlaying && currentMood === adjective) {
    return; // Already playing this mood
  }
  
  // Stop any existing sounds
  if (isPlaying) {
    stopAmbientSound(1.0);
    setTimeout(() => startNewSound(ctx, adjective, intensity, volume), 1100);
  } else {
    startNewSound(ctx, adjective, intensity, volume);
  }
};

const startNewSound = (ctx, adjective, intensity, volume) => {
  const quadrant = getQuadrantForMood(adjective);
  const profile = MOOD_PROFILES[quadrant];
  const wordIntensity = WORD_INTENSITY_MAP[adjective?.toLowerCase()] || 1.0;
  const effectiveIntensity = Math.min(1, intensity * wordIntensity);
  
  const now = ctx.currentTime;
  currentNodes = [];
  currentMood = adjective;
  
  // Master filter for overall tone shaping
  const masterFilter = ctx.createBiquadFilter();
  masterFilter.type = 'lowpass';
  masterFilter.frequency.value = profile.filterFreq;
  masterFilter.Q.value = profile.filterQ;
  masterFilter.connect(masterGain);
  
  // Create layers based on profile
  profile.layers.forEach(layerType => {
    let layerNodes = [];
    
    switch (layerType) {
      case 'drone':
        layerNodes = createDroneLayer(ctx, profile.baseFrequency, effectiveIntensity);
        break;
      case 'pad':
        layerNodes = createPadLayer(ctx, profile.baseFrequency, profile.waveform, effectiveIntensity);
        break;
      case 'shimmer':
        layerNodes = createShimmerLayer(ctx, profile.baseFrequency, effectiveIntensity);
        break;
      case 'rain':
        layerNodes = createRainLayer(ctx, effectiveIntensity);
        break;
      case 'wind':
        layerNodes = createWindLayer(ctx, effectiveIntensity);
        break;
      case 'waves':
        layerNodes = createWavesLayer(ctx, effectiveIntensity);
        break;
      case 'birds':
        layerNodes = createBirdsLayer(ctx, effectiveIntensity);
        break;
      case 'thunder':
        layerNodes = createThunderLayer(ctx, effectiveIntensity);
        break;
      default:
        break;
    }
    
    // Connect all layer nodes to master filter
    layerNodes.forEach(node => {
      if (node.gain) {
        node.gain.connect(masterFilter);
      }
      currentNodes.push(node);
    });
  });
  
  // Start all sources
  currentNodes.forEach(node => {
    try {
      if (node.source) node.source.start(now);
      if (node.lfo) node.lfo.start(now);
      if (node.ampLfo) node.ampLfo.start(now);
    } catch (e) {
      console.warn('Error starting audio node:', e);
    }
  });
  
  // Fade in master gain
  masterGain.gain.setValueAtTime(0, now);
  masterGain.gain.linearRampToValueAtTime(volume, now + 2);
  
  isPlaying = true;
};

// Set master volume
export const setVolume = (vol) => {
  if (masterGain && audioContext) {
    masterGain.gain.setTargetAtTime(vol, audioContext.currentTime, 0.1);
  }
};

// Check if audio is playing
export const isAudioPlaying = () => isPlaying;

// Get current playing mood
export const getCurrentMood = () => currentMood;

export default {
  initAudioContext,
  playAmbientSound,
  stopAmbientSound,
  setVolume,
  isAudioPlaying,
  getQuadrantForMood,
  getCurrentMood,
};
